import { useEffect, useRef, useState } from 'react';
import { getCurrentUser } from '../utils/userStorage';
import { refreshAuthToken } from '../utils/tokenRefresh';

/**
 * useCurrentUser
 * Returns the current user from local storage. If any required fields are
 * missing, performs a one-time token refresh (which also fetched and persists the refreshed user)
 * and then re-reads the user from storage.
 *
 * Example:
 *   const user = useCurrentUser(['referral_code']);
 */
export function useCurrentUser(requiredKeys = [], trigger = 0) {
  const [user, setUser] = useState(() => {
    try { return getCurrentUser(); } catch { return null; }
  });
  const hasAttemptedRefreshRef = useRef(false);

  const areRequiredKeysMissing = (u, keys) => {
    if (!keys || keys.length === 0) return false;
    if (!u) return true;
    return keys.some((key) => !u[key]);
  };

  // Re-read user from storage whenever external trigger changes
  useEffect(() => {
    try { setUser(getCurrentUser()); } catch { setUser(null); }
  }, [trigger]);

  // If required fields are missing, try one-time refresh
  useEffect(() => {
    const isMissingRequired = areRequiredKeysMissing(user, requiredKeys);
    if (!isMissingRequired || hasAttemptedRefreshRef.current) return;

    hasAttemptedRefreshRef.current = true;
    let isMounted = true;

    (async () => {
      try {
        const newToken = await refreshAuthToken();
        if (!newToken) return;
        const refreshed = getCurrentUser();
        if (isMounted) setUser(refreshed);
      } catch (_) {
        // swallow errors; UI can proceed with partial user
      }
    })();

    return () => { isMounted = false; };
  }, [user, JSON.stringify(requiredKeys)]);

  return user;
}


